// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { AERIAL_IMAGERY_PROVIDERS } from 'Features/order/OrderForm/Package/DataSource/dataSection';
import { catalogOptionKey } from './catalogOption';

// A cart of catalog products read as the one selection the order form knows (AUT-6630 D6, "start simple"): the
// combined name and the quote's cart figures, the union of included layers and of available add-ons (D13), the first
// product's id and subtype for the legacy readers, and the cart's data classes. One product is itself.

const unionById = (lists: IDeliverable[][]): IDeliverable[] => {
  const seen = new Set<string>();
  return lists.flat().filter((deliverable) => {
    if (seen.has(deliverable._id)) return false;
    seen.add(deliverable._id);
    return true;
  });
};

const sum = (values: (number | undefined)[]): number => Math.round(values.reduce((total: number, value) => total + (value ?? 0), 0) * 100) / 100;

// The minimum project fee over the floored products together (AUT-6630 D22), from each product's own quote: a floored
// product's fee plus its total is the floor; the fee is the floor less the floored products' totals, never the others'
const flooredTogether = (products: IBundle[]): { deploymentFeeDollar: number; deploymentFeeProducts: string[] } => {
  const floored = products.filter((product) => (product.deploymentFeeDollar ?? 0) > 0);
  if (!floored.length) return { deploymentFeeDollar: 0, deploymentFeeProducts: [] };
  const floor = (floored[0].deploymentFeeDollar ?? 0) + (floored[0].bundleCost ?? 0);
  return {
    deploymentFeeDollar: Math.max(0, Math.round((floor - sum(floored.map((product) => product.bundleCost))) * 100) / 100),
    deploymentFeeProducts: floored.map((product) => product.name),
  };
};

// A quoted line's list prices and the discount rows the quote applied to them, when the quote priced it from them (a
// contracted bundle card is a figure and carries none)
export const listPricesOf = (line: ICatalogProduct | ICatalogCartProduct): Pick<IBundle, 'extractionDollarBeforeDiscount' | 'dataDollarBeforeDiscount' | 'discountRows'> => (
  typeof line.extractionDollarBeforeDiscount === 'number'
    ? { extractionDollarBeforeDiscount: line.extractionDollarBeforeDiscount, dataDollarBeforeDiscount: line.dataDollarBeforeDiscount ?? 0, discountRows: line.discountRows ?? [] }
    : {}
);

const sameProducts = (ids: string[], others: string[]): boolean => ids.length === others.length && ids.every((id) => others.includes(id));

// The products behind the ticked keys, in list (catalog) order; a bundle key names no product
export const productsForKeys = (groupings: IBundle[], keys: string[]): IBundle[] => (
  groupings.filter((grouping) => !grouping.customBundleId && keys.includes(catalogOptionKey(grouping)))
);

// What the save sends as the cart: every product of a combination, or the one selection's id
export const cartProductIds = (selectedBundle: IBundle): string[] => (
  selectedBundle.productIds?.length ? selectedBundle.productIds : [selectedBundle._id]
);

export const combineCatalogProducts = (products: IBundle[], cart: ICatalogCart | null): IBundle | null => {
  if (!products.length) return null;
  if (products.length === 1) return products[0];

  // Catalog order (D7: Clearances & Violations last): the cart block names it; the list carries it otherwise
  const ids = products.map((product) => product._id);
  const quoted = cart && sameProducts(ids, cart.productIds.map(String)) ? cart : null;
  const ordered = quoted
    ? [...products].sort((a, b) => quoted.productIds.indexOf(a._id) - quoted.productIds.indexOf(b._id))
    : products;
  const [first] = ordered;
  const names = ordered.map((product) => product.name);
  const dataProviders = Array.from(new Set(ordered.map((product) => product.dataProvider).filter((provider): provider is string => !!provider)));
  // One class for the readers that know one (the KML-only gate holds when any class is marketplace aerial, as the
  // server does); the set is for the data step (Chunk 5)
  const dataProvider = dataProviders.find((provider) => AERIAL_IMAGERY_PROVIDERS.includes(provider)) ?? dataProviders[0] ?? null;

  // The quote's cart figures when it priced these products together; the products' own figures summed until it has
  // (browse mode, or a quote that predates the tick): provisional, corrected by the next quote
  const figures = quoted
    ? {
      bundleCost: quoted.afterDiscountDollar ?? 0,
      priceCredit: quoted.afterDiscountCredit ?? 0,
      contactForPricing: !quoted.priceable,
      priceBeforeDiscountDollar: quoted.totalBeforeDiscountDollar ?? 0,
      discountDollar: quoted.discountDollar ?? 0,
      promotionDollar: quoted.promotionDollar ?? 0,
      dollarPerCredit: quoted.dollarPerCredit,
      tierName: quoted.tierName,
      discountPercent: quoted.discountPercent,
      nextTierTeaser: quoted.nextTierTeaser ?? null,
      deploymentFeeDollar: quoted.deploymentFeeDollar ?? 0,
      deploymentFeeProducts: quoted.deploymentFeeProducts ?? [],
      dataProviderDollar: quoted.dataProviderDollar ?? 0,
      dataProviderCredit: quoted.dataProviderCredit ?? 0,
      extractionDollar: quoted.extractionDollar ?? 0,
      extractionCredit: quoted.extractionCredit ?? 0,
    }
    : {
      bundleCost: sum(ordered.map((product) => product.bundleCost)),
      priceCredit: sum(ordered.map((product) => product.priceCredit)),
      contactForPricing: ordered.some((product) => !!product.contactForPricing),
      priceBeforeDiscountDollar: sum(ordered.map((product) => product.priceBeforeDiscountDollar)),
      discountDollar: sum(ordered.map((product) => product.discountDollar)),
      promotionDollar: sum(ordered.map((product) => product.promotionDollar)),
      dollarPerCredit: first.dollarPerCredit,
      tierName: first.tierName,
      discountPercent: first.discountPercent,
      nextTierTeaser: null,
      ...flooredTogether(ordered),
      dataProviderDollar: sum(ordered.map((product) => product.dataProviderDollar)),
      dataProviderCredit: sum(ordered.map((product) => product.dataProviderCredit)),
      extractionDollar: sum(ordered.map((product) => product.extractionDollar)),
      extractionCredit: sum(ordered.map((product) => product.extractionCredit)),
    };

  // The pole line reads the corridor's poles: the first pole product's estimate (every pole product shares the density)
  const poleCarrier = ordered.find((product) => !!product.poleSpacingFt && !!product.estimatedPoles);

  return {
    ...first,
    name: quoted?.name || names.join(' + '),
    productIds: ordered.map((product) => product._id),
    poleSpacingFt: poleCarrier?.poleSpacingFt,
    estimatedPoles: poleCarrier?.estimatedPoles,
    // each product's list figures ride along for the breakdown's line items (Joel, 2026-10-19): the quote's when it
    // priced the cart (a covered product's data left out, D18), the product's own until then
    products: ordered.map((product) => {
      const priced = quoted?.products.find((cartProduct) => String(cartProduct._id) === product._id);
      return {
        _id: product._id,
        name: product.name,
        dataProvider: product.dataProvider ?? null,
        subtype: product.subtype,
        priceBeforeDiscountDollar: priced?.totalBeforeDiscountDollar ?? product.priceBeforeDiscountDollar ?? 0,
        dataProviderDollar: priced?.dataProviderDollar ?? product.dataProviderDollar ?? 0,
        // the line's list prices and discount rows, the quote's at the cart's tier, else the product's own
        ...(priced ? listPricesOf(priced) : {
          extractionDollarBeforeDiscount: product.extractionDollarBeforeDiscount,
          dataDollarBeforeDiscount: product.dataDollarBeforeDiscount,
          discountRows: product.discountRows,
        }),
      };
    }),
    bundleDeliverables: unionById(ordered.map((product) => product.bundleDeliverables ?? [])),
    additionalDeliverables: unionById(ordered.map((product) => product.additionalDeliverables ?? [])),
    dataProvider,
    dataProviders,
    customBundleId: undefined,
    parentProductName: undefined,
    contractedPricePerFoot: undefined,
    contractedDollar: undefined,
    // each product prices as its own line (products, above): the first product's list prices are not the cart's
    extractionDollarBeforeDiscount: undefined,
    dataDollarBeforeDiscount: undefined,
    discountRows: undefined,
    sizeBand: first.sizeBand ?? null,
    bufferMultiplier: first.bufferMultiplier ?? 1,
    ...figures,
  };
};

export default combineCatalogProducts;
